<script setup>
import RegionItem from '@/components/PartnerList/RegionItem.vue'
import { computed, ref, nextTick, watch, defineAsyncComponent, useTemplateRef } from 'vue'
import CityPin from '@/components/PartnerList/CityPin.vue'
import AccommodationItem from '@/components/PartnerList/AccommodationItem.vue'
const props = defineProps({
  regions: Array,
  counts: Object,
  accommodations: Object,
})

const InteractiveMap = defineAsyncComponent(
  () => import(/* webpackChunkName: "interactive-map-chunk" */ '../../map/InteractiveMap'),
)

const navWrapper = useTemplateRef('navWrapper')

const filters = ref({
  region: null,
})

// nav scroll
watch(
  filters,
  (filters) => {
    let id = `nav-region`

    if (filters.region !== null) {
      id += `-${filters.region}`
    }

    return nextTick(() => {
      const yPos = navWrapper.value.querySelector(`#${id}`)?.offsetTop ?? 0
      const stickyHeaderHeight = 80

      return navWrapper.value.scroll(0, Math.max(yPos - stickyHeaderHeight, 0))
    })
  },
  { deep: true },
)

const selectedRegionSlug = computed({
  get() {
    if (filters.value.region === null) {
      return null
    }

    return props.regions.find(({ id }) => id === filters.value.region).slug
  },
  set(slug) {
    if (slug === null) {
      filters.value.region = null
      return
    }

    const region = props.regions.find((region) => slug === region.slug)

    if (region === undefined) {
      return
    }

    filters.value.region = filters.value.region !== region.id ? region.id : null
  },
})

const toggleRegion = (region) => {
  filters.value.region = filters.value.region !== region ? region : null
}
</script>

<template>
  <div
    class="mt-8 grid select-none grid-cols-1 lg:grid-cols-[minmax(320px,1fr)_minmax(640px,780px)] lg:grid-rows-[minmax(640px,780px)]">
    <nav
      ref="navWrapper"
      class="relative snap-y scroll-py-2 overflow-y-scroll scroll-smooth lg:max-h-[780px]">
      <ul id="nav-region">
        <template v-for="region in regions">
          <li
            v-if="counts.regions[region.id] > 0"
            :id="`nav-region-${region.id}`"
            :key="region.id"
            class="snap-start">
            <RegionItem
              class="z-20 border-b border-light/25"
              :selected="filters.region === region.id"
              :region="region"
              :count="counts.regions[region.id]"
              @click="toggleRegion(region.id)" />
            <ul
              v-show="filters.region === region.id"
              class="mt-4 space-y-4">
              <li
                v-for="(cityAccommodations, city) in accommodations[region.id]"
                :key="city"
                class="snap-start">
                <CityPin :name="city" />
                <ul class="mt-1 space-y-2">
                  <li
                    v-for="accommodation in cityAccommodations"
                    :key="accommodation.id"
                    class="snap-start">
                    <AccommodationItem :accommodation="accommodation" />
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </template>
      </ul>
    </nav>
    <InteractiveMap
      id="svg-map"
      v-model="selectedRegionSlug"
      class="order-first aspect-square rounded-xl bg-lightgray lg:order-last" />
  </div>
</template>
